import {
  endOfDay,
  format,
  formatISO,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subYears,
} from 'date-fns';
import { STANDARD_DATE_FORMAT } from '@/configs';

function parseUtcIsoDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatUtcIsoDateTimeLocal(
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return '';
  const date = parseUtcIsoDate(value);
  if (!date) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

/**
 * .NET <c>DateTime.MinValue</c> / legacy migration "unknown date" sentinel
 * (<c>0001-01-01</c>). Stored as a real string in EntityDetailValue but must
 * render and bind as empty — same treatment as backend <c>isEmptyDate</c>.
 */
export function isUnsetDateSentinel(value: unknown): boolean {
  if (value == null || value === '') return false;

  if (value instanceof Date) {
    if (!isValid(value)) return false;
    // Local or UTC: timezone can shift ISO midnight across the day boundary.
    return (
      (value.getFullYear() === 1 &&
        value.getMonth() === 0 &&
        value.getDate() === 1) ||
      (value.getUTCFullYear() === 1 &&
        value.getUTCMonth() === 0 &&
        value.getUTCDate() === 1)
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    // ISO / SQL-style and dd/MM/yyyy or MM/dd/yyyy for 1 Jan year 1
    if (/^0001-01-01\b/.test(trimmed)) return true;
    if (/^01\/01\/0001\b/.test(trimmed)) return true;
    const parsed = parseISO(trimmed);
    if (isValid(parsed)) return isUnsetDateSentinel(parsed);
    const fallback = new Date(trimmed);
    return isUnsetDateSentinel(fallback);
  }

  return false;
}

export function formatDateISOString(
  dateString: string,
  dateFormat: string = STANDARD_DATE_FORMAT,
): string {
  if (isUnsetDateSentinel(dateString)) return '';
  const date = parseISO(dateString);
  return isValid(date) ? format(date, dateFormat) : '';
}

export function formatDob(
  dob: string | null | undefined,
  dateFormat: string = STANDARD_DATE_FORMAT,
): string {
  if (!dob || isUnsetDateSentinel(dob)) return '—';
  const date = parseISO(dob);
  return isValid(date) ? format(date, dateFormat) : dob;
}

export function formatDate(
  date: Date,
  dateFormat: string = STANDARD_DATE_FORMAT,
): string {
  if (!isValid(date) || isUnsetDateSentinel(date)) return '';
  return format(date, dateFormat);
}

export function ISODateStringToDate(
  isoDateString: string | undefined,
): Date | undefined {
  if (!isoDateString || isUnsetDateSentinel(isoDateString)) return undefined;
  const date = parseISO(isoDateString);
  return isValid(date) ? date : undefined;
}

export function toISODateString(date: string | number | Date): string {
  if (isUnsetDateSentinel(date)) return '';
  const parsed = new Date(date);
  return isValid(parsed) ? formatISO(parsed, { representation: 'date' }) : '';
}

export function isValidDate(date: any): boolean {
  if (!date || isUnsetDateSentinel(date)) return false;
  return isValid(new Date(date));
}

/**
 * ISO string for the start/end instant of a date's day, or undefined when the
 * date is absent or invalid (e.g. a half-typed date picker value). Useful for
 * building inclusive date-range query params.
 */
export function toIsoDayBoundary(
  date: Date | null | undefined,
  edge: 'start' | 'end',
): string | undefined {
  if (!date || !isValid(date)) return undefined;
  return (edge === 'start' ? startOfDay(date) : endOfDay(date)).toISOString();
}

export const toDateValue = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  return new Date(value as string);
};

export const dateWithoutTime = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Rolling minimum valid calendar date for animal sanity date rules, aligned with
 * backend `RuleEvaluationService.GetRidiculouslyOldCutoffDate` (local today at
 * midnight minus 150 years).
 */
export function getRidiculouslyOldCutoffDate(): Date {
  return startOfDay(subYears(new Date(), 150));
}

/** MUI X v8 sectioned date field root (PickersSectionList). */
const PICKERS_SECTION_LIST_ROOT = '.MuiPickersSectionList-root';

/**
 * Canonical message shown for any "user has not produced a valid date" state
 * on a required date field — empty, partially typed, or impossible-date
 * (e.g. `00/00/0000`). Used by both client-side validation paths (blur and
 * Zod) and is intended to take precedence over server-supplied messages
 * (e.g. "Date of Birth is required") so the experience is consistent.
 */
export const INVALID_DATE_MESSAGE = 'Please enter a valid date.';

/**
 * Visible text from a MUI PickersTextField (section list). Used on blur when
 * the committed value can stay `null` while the user typed invalid/partial input.
 */
function getDisplayStringFromPickerFormControl(
  formControlEl: HTMLElement,
): string {
  const root = formControlEl.querySelector(PICKERS_SECTION_LIST_ROOT);
  if (!root) return '';
  return (root.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

export type PickerDisplayBlurResult =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'minDate' }
  | { kind: 'maxDate' }
  | { kind: 'valid' };

export type PickerDisplayBlurBounds = {
  minDate?: Date;
  maxDate?: Date;
};

/**
 * Validate what the user sees in the picker field vs `STANDARD_DATE_FORMAT`
 * (`dd/MM/yyyy`). If there are no digits, treat as empty (placeholders only).
 */
export function validatePickerDisplayOnBlur(
  formControlEl: HTMLElement,
  bounds?: PickerDisplayBlurBounds,
): PickerDisplayBlurResult {
  const display = getDisplayStringFromPickerFormControl(formControlEl);
  if (!/\d/.test(display)) {
    return { kind: 'empty' };
  }
  const parsed = parse(display, STANDARD_DATE_FORMAT, new Date());
  if (!isValid(parsed)) {
    return { kind: 'invalid' };
  }
  const day = dateWithoutTime(parsed);
  if (bounds?.minDate && day < dateWithoutTime(bounds.minDate)) {
    return { kind: 'minDate' };
  }
  if (bounds?.maxDate && day > dateWithoutTime(bounds.maxDate)) {
    return { kind: 'maxDate' };
  }
  return { kind: 'valid' };
}
